import { Locale } from "src/sanity/types"
import { approvalRatePerIndustryCode } from "./approvalRatePerIndustryCode"
import { CVRInfo } from "./cvr"
import { cvrIndustryCodeToFcpRiskScore } from "./fcpRiskScore"

export async function sendHubspotRequest(path, method, body?: any) {
    const options = {
        method,
        ...(body ? { body: JSON.stringify(body) } : {}),
        headers: {
            accept: "application/json",
            "content-type": "application/json",
            authorization: "Bearer pat-eu1-9519c356-d203-4aaa-a24b-3a4d9a39207c",
        },
    }
    try {
        const requestRes = await (await fetch("https://api.hubapi.com/crm/v3" + path, options)).json()
        // const starting_after = requestRes?.pages?.next?.starting_after
        // if (starting_after) {
        //     const nextRequestRes = await sendHubspotRequest(path, method, { ...body, pagination: { ...body?.pagination, starting_after } })
        //     requestRes.data.push(...nextRequestRes.data)
        // }
        // if (requestRes.errors) throw new Error(JSON.stringify(requestRes.errors))
        if (requestRes.status === "error") {
            if (
                requestRes.message.includes("already has that value") ||
                requestRes.message.includes("Contact already exists.")
            ) {
                console.log("Company already exists with this CVR:", requestRes.message)
                return { status: "already_exists" }
            }
            throw new Error(JSON.stringify(requestRes))
        }

        // console.log("Sucessfully made request to Hubspot", method, path)
        return requestRes
    } catch (error) {
        if (error?.message?.includes("INVALID_EMAIL")) {
            console.log("Invalid formatted email provided to Hubspot:", error.message)
        }
        console.error("Error in hubspot:sendHubspotRequest:", method, path, error)
        return { status: "error", error }
    }
}

export const InitialSalesOutcomeMap = {
    Assigned: "Prepped",
    AttemptingContact: "Ongoing",
    ConnectedFollowUp: "Connected to lead",
    DealCreatedLunar: "Deal Created (Lunar)",
    UnsuccessfulOutreach: "Not interested",
    ValidRejection: "Rejection",
    NoConnectionFinal: "No Connection (Final)",
    Irrelevant: "Irrelevant",
    ExpiredLead: "Expired Lead",
    UpgradeWon: "Upgraded",
    UpgradeLost: "Upgrade Lost",
    DealCreatedRadiant: "Deal created",
} as const

// Type of all possible string values
export type InitialSalesOutcome = (typeof InitialSalesOutcomeMap)[keyof typeof InitialSalesOutcomeMap]
export interface HubspotCompany {
    name?: string
    cvr?: number
    phone?: string
    email?: string
    /** Whether the company has requested in CVR/Virk to not be contacted for advertisement */
    advertisement_protected?: boolean
    /** (Houston) Whether the company has a matching user in Lunar which have given consent has given marketing consent to Lunar – used for new-cvr leads */
    marketing_consent?: boolean
    company_type?: string
    registered_industry_code?: number
    registered_industry?: string
    registered_industry_category?: string
    founded_date?: number
    lunar_lead_source?: string
    lunar_website_lead_signup_page?: string
    lunar_website_lead_session_source?: string
    lunar_user_id?: string
    lead_language?: Locale
    city?: string
    ownership_information?: string
    industry_based_approval_rate____?: number
    industry_risk_score?: number
    website?: string
    boarding_started?: number
    boarding_applied?: number
    boarding_approved?: number
    boarding_rejected?: number
    purchased_tier?: string
    arpu_class_estimate?: number
    lead_note?: string
    customer_relations__lunar_vs__non_lunar_?: string
    rejection_reasons?: string
    initial_sales_notes?: string
    cvr_company_purpose?: string
    initial_sales_outcome?: InitialSalesOutcome
    initial_sales_outcome_drill_down?: string
    num_associated_deals?: number
    first_deal_created_date?: number
}

export async function getDealProperties() {
    const res = await sendHubspotRequest("/properties/deals", "GET")
    return res
}
export async function getCompanyProperties() {
    const res = await sendHubspotRequest("/properties/companies", "GET")
    // return res.results.map((property: any) => ({
    //     name: property.name,
    //     label: property.label,
    //     type: property.type,
    //     description: property.description,
    //     groupName: property.groupName,
    // }))
    return res
}
export async function getDealsAssociatedToCompany(companyId: string) {
    const res = await sendHubspotRequest("/objects/companies/" + companyId + "/associations/deals", "GET")
    return res
}

export async function getCompany(companyId: string): Promise<{ properties: HubspotCompany }> {
    const res = await sendHubspotRequest(
        "/objects/companies/" +
            companyId +
            "?properties=initial_sales_outcome,num_associated_deals,first_deal_created_date",
        "GET",
    )
    return res
}

export async function createCompany(properties: HubspotCompany) {
    const res = await sendHubspotRequest("/objects/companies", "POST", { properties })
    return res
}

export interface HubspotContact {
    firstname?: string
    lastname?: string
    phone?: string
    email?: string
    domain?: string
    number_of_owners?: number
}
export async function createContact(properties: HubspotContact, associations?: any) {
    const res = await sendHubspotRequest("/objects/contacts", "POST", {
        properties,
        ...(associations ? { associations } : {}),
    })
    return res
}

export async function createCompanyWithContacts(
    companyProperties: HubspotCompany,
    contactsProperties: HubspotContact[],
    companyNote?: string,
) {
    const companyRes = await createCompany(companyProperties)
    if (!companyRes.id) return { companyRes }

    if (companyNote) {
        await sendHubspotRequest("/objects/notes", "POST", {
            properties: {
                hs_timestamp: new Date().getTime(),
                hs_note_body: companyNote,
                hubspot_owner_id: "1570259185", // "1570259185" is Andreas Poulsen, "1554939869" is Andreas Tegam Mortensen
            },
            associations: [
                {
                    "to": {
                        "id": companyRes.id,
                    },
                    "types": [
                        {
                            "associationCategory": "HUBSPOT_DEFINED",
                            "associationTypeId": 190, // Note to company
                        },
                    ],
                },
            ],
        })
    }

    for await (const contactProperty of contactsProperties || []) {
        await createContact(contactProperty, [
            {
                "to": {
                    "id": companyRes.id,
                },
                "types": [
                    {
                        "associationCategory": "HUBSPOT_DEFINED",
                        "associationTypeId": 279, // Contact to company
                    },
                ],
            },
        ])
    }

    return { companyRes }
}

export function cvrInfoToHubspotCompany(cvrInfo: Partial<CVRInfo>): Partial<HubspotCompany> {
    return {
        name: cvrInfo?.name,
        cvr: Number(cvrInfo.cvr),
        advertisement_protected: cvrInfo?.protected,
        company_type: cvrInfo?.companyType,
        registered_industry_code: Number(cvrInfo?.industryCode),
        registered_industry: cvrInfo?.industryName,
        registered_industry_category: cvrInfo?.industryCategory,
        cvr_company_purpose: cvrInfo?.purpose,
        founded_date: convertDateToHubspotDate(new Date(cvrInfo?.foundedDateTimestamp)),
        city: cvrInfo?.city,
        ownership_information: (cvrInfo?.owners || []).join(", "),
        industry_based_approval_rate____: approvalRatePerIndustryCode[cvrInfo?.industryCode],
        industry_risk_score: cvrIndustryCodeToFcpRiskScore[cvrInfo?.industryCode],
        website: cvrInfo?.website,
    }
}

export async function updateCompany(id: string, properties: Partial<HubspotCompany>) {
    const res = await sendHubspotRequest("/objects/companies/" + id, "PATCH", { properties })
    return res
}

export async function deleteCompany(id: string) {
    console.log("Deleting company in Hubspot with id", id)
    const res = await sendHubspotRequest("/objects/companies/" + id, "DELETE")
    return res
}

// Date has to be formatted as a unix timestamp, dated at midnight UTC
export function convertDateToHubspotDate(date: Date) {
    // Set the date to midnight UTC
    date.setUTCHours(0, 0, 0, 0)
    return date.getTime()
}
